import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Divider, Grid, Stack, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { FormSelect, FormTextField } from "@zfp-akademie-a-s/zfp-design/form";
import { FormNumberField } from "@zfp-akademie-a-s/zfp-design/form/number-field";
import { AnalysisDetailContext } from "Components/Analyses/Detail/AnalysisDetailContext";
import { NOTE_MAX_LENGTH } from "Components/Analyses/constants";
import { type OtherFormType, getOtherFormSchema } from "Forms/Schemas/GoalsFulfillment/CurrentSolution/otherFormSchema";
import { type Option, toSelectOptions } from "Forms/fieldOptions";
import {
  getGetAssetAllocationOverviewQueryKey,
  getListCurrentContractsQueryKey,
  useCreateCurrentContract,
  usePatchCurrentContract,
} from "Infrastructure/ApiZfp/AnalyzerApi";
import {
  AmountFrequency,
  CreateCurrentContractOtherBodyInvestmentToolType,
  type CreateCurrentContractOtherBodyProposedStepActionType,
  type CreateCurrentContractOtherBodySolutionHorizonType,
  type CurrentContractBaseInvestmentToolType,
  type CurrentContractBaseSolutionHorizonType,
  type CurrentContractDetailOther,
  CurrentContractListItemProposedStepActionType,
  type CurrentSolutionAssetOption,
  PatchCurrentContractOtherBodyInvestmentToolType,
  type PatchCurrentContractOtherBodyProposedStepActionType,
} from "Infrastructure/ApiZfp/Schema";
import { CurrencyCode } from "Infrastructure/Models/CurrencyCodes";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { forwardRef, useContext, useEffect, useImperativeHandle, useRef } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "sonner";
import { useGoalsFulfillmentContext } from "../../GoalsFulfillmentContext";
import { CharacteristicsAccordion } from "../CharacteristicsAccordion";
import type { CurrentSolutionFormHandle } from "./CurrentSolutionWrapperForm";
import { ModifyDepositFields } from "./ModifyDepositFields";

type Props = {
  readonly edit?: boolean;
  readonly investmentToolType: CurrentContractBaseInvestmentToolType;
  readonly institution?: string;
  readonly horizonType: CurrentContractBaseSolutionHorizonType;
  readonly onSuccess?: () => void;
  readonly currentSolutionId?: string;
  readonly initialData?: CurrentContractDetailOther | null;
};

export const OtherForm = forwardRef<CurrentSolutionFormHandle, Props>(
  ({ investmentToolType, institution, edit, horizonType, onSuccess, currentSolutionId, initialData }, ref) => {
    const { t } = useResource();
    const queryClient = useQueryClient();
    const institutionRef = useRef(institution);
    const { analysisId, versionId } = useContext(AnalysisDetailContext);
    const { assetAllocationOverview } = useGoalsFulfillmentContext();

    const { mutate: createMutation } = useCreateCurrentContract();
    const { mutate: updateMutation } = usePatchCurrentContract();

    const proposedStepActionOptions: Option[] = (
      [
        CurrentContractListItemProposedStepActionType.Keep,
        CurrentContractListItemProposedStepActionType.Modify,
        CurrentContractListItemProposedStepActionType.Cancel,
      ] as Array<keyof typeof CurrentContractListItemProposedStepActionType>
    ).map((value) => ({
      value,
      name: t(Resources.AnalysisDetail.GoalsFulfillment.ProposedStepActionTypeOptions[value]),
    }));

    const methods = useForm<OtherFormType>({
      resolver: yupResolver(getOtherFormSchema(t, assetAllocationOverview?.isPortfolioAssetAllocationRequired)),
      mode: "onChange",
      defaultValues:
        edit && initialData
          ? {
              investmentToolType: initialData.investmentToolType as any,
              institution: initialData.partnerName ?? "",
              currentValue: initialData.oneTimeAmount?.value ?? 0,
              regularAmount: initialData.regularAmount?.value ?? 0,
              horizon: initialData.currentSolutionHorizon ?? 0,
              appreciationRate: initialData.appreciationRate ?? 0,
              equities: initialData.assetAllocation?.equities ?? 0,
              bonds: initialData.assetAllocation?.bonds ?? 0,
              commodities: initialData.assetAllocation?.commodities ?? 0,
              realEstate: initialData.assetAllocation?.realEstate ?? 0,
              cash: initialData.assetAllocation?.cash ?? 0,
              other: initialData.assetAllocation?.others ?? 0,
              entryFee: initialData.entryFee ?? 0,
              oneTimeFee: initialData.oneTimeFee?.value ?? 0,
              managementFee: initialData.managementFee ?? 0,
              exitFee: initialData.exitFee ?? 0,
              proposedStepAction: initialData.proposedStepActionType,
              newDeposit: (initialData.oneTimeAmount?.value ?? 0) + (initialData.oneTimeAmountChange?.value ?? 0),
              newRegularDeposit: (initialData.regularAmount?.value ?? 0) + (initialData.regularAmountChange?.value ?? 0),
              note: initialData.note ?? "",
              proposedSolutionHorizon: initialData.proposedSolutionHorizon ?? 0,
              proposedSolutionEntryFee: initialData.proposedSolutionEntryFee ?? 0,
              proposedSolutionManagementFee: initialData.proposedSolutionManagementFee ?? 0,
            }
          : {
              investmentToolType,
              institution,
            },
    });

    const { control, watch, handleSubmit, reset, setValue, getValues } = methods;

    const proposedStepAction = watch("proposedStepAction");

    useEffect(() => {
      if (edit && initialData) {
        reset({
          investmentToolType: initialData.investmentToolType as any,
          institution: institutionRef.current,
          currentValue: initialData.oneTimeAmount?.value ?? 0,
          regularAmount: initialData.regularAmount?.value ?? 0,
          horizon: initialData.currentSolutionHorizon ?? 0,
          appreciationRate: initialData.appreciationRate ?? 0,
          equities: initialData.assetAllocation?.equities ?? 0,
          bonds: initialData.assetAllocation?.bonds ?? 0,
          commodities: initialData.assetAllocation?.commodities ?? 0,
          realEstate: initialData.assetAllocation?.realEstate ?? 0,
          cash: initialData.assetAllocation?.cash ?? 0,
          other: initialData.assetAllocation?.others ?? 0,
          entryFee: initialData.entryFee ?? 0,
          oneTimeFee: initialData.oneTimeFee?.value ?? 0,
          managementFee: initialData.managementFee ?? 0,
          exitFee: initialData.exitFee ?? 0,
          proposedStepAction: initialData.proposedStepActionType,
          newDeposit: (initialData.oneTimeAmount?.value ?? 0) + (initialData.oneTimeAmountChange?.value ?? 0),
          newRegularDeposit: (initialData.regularAmount?.value ?? 0) + (initialData.regularAmountChange?.value ?? 0),
          note: initialData.note ?? "",
          proposedSolutionHorizon: initialData.proposedSolutionHorizon ?? 0,
          proposedSolutionEntryFee: initialData.proposedSolutionEntryFee ?? 0,
          proposedSolutionManagementFee: initialData.proposedSolutionManagementFee ?? 0,
        });
      }
    }, [edit, initialData, reset]);

    useEffect(() => {
      setValue("institution", institution ?? "", { shouldValidate: true });
    }, [institution, setValue]);

    useImperativeHandle(ref, () => ({
      submitForm: () => {
        if (edit) {
          handleSubmit(onEdit)();
        } else {
          handleSubmit(onCreate)();
        }
      },
      prefillFromAssetOption: (asset: CurrentSolutionAssetOption) => {
        setValue("investmentToolType", asset.investmentToolType as any, { shouldValidate: true, shouldDirty: true });
        setValue("currentValue", asset.amount?.value ?? 0, { shouldValidate: true, shouldDirty: true });
        setValue("regularAmount", asset.regularAmount?.value ?? 0, { shouldValidate: true, shouldDirty: true });
      },
    }));

    const onCreate = (data: OtherFormType) => {
      const isProposedStepModify = data.proposedStepAction === CurrentContractListItemProposedStepActionType.Modify;
      createMutation(
        {
          analysisId,
          versionId,
          data: {
            investmentToolType: CreateCurrentContractOtherBodyInvestmentToolType.other,
            solutionHorizonType: horizonType as CreateCurrentContractOtherBodySolutionHorizonType,
            proposedStepActionType: data.proposedStepAction as CreateCurrentContractOtherBodyProposedStepActionType,
            partnerName: institution ?? "",
            oneTimeAmount: {
              value: data.currentValue,
              currencyCode: CurrencyCode.CZK,
              frequency: AmountFrequency.one_time,
            },
            regularAmount: {
              value: data.regularAmount,
              currencyCode: CurrencyCode.CZK,
              frequency: AmountFrequency.monthly,
            },
            oneTimeAmountChange: isProposedStepModify
              ? {
                  value: (data.newDeposit ?? 0) - data.currentValue,
                  currencyCode: CurrencyCode.CZK,
                  frequency: AmountFrequency.one_time,
                }
              : undefined,
            regularAmountChange: isProposedStepModify
              ? {
                  value: (data.newRegularDeposit ?? 0) - data.regularAmount,
                  currencyCode: CurrencyCode.CZK,
                  frequency: AmountFrequency.monthly,
                }
              : undefined,
            assetAllocation: {
              equities: data.equities ?? 0,
              bonds: data.bonds ?? 0,
              commodities: data.commodities ?? 0,
              realEstate: data.realEstate ?? 0,
              cash: data.cash ?? 0,
              others: data.other ?? 0,
            },
            currentSolutionHorizon: data.horizon ?? 0,
            appreciationRate: data.appreciationRate,
            note: data.note ?? "",
            oneTimeFee: {
              value: data.oneTimeFee ?? 0,
              currencyCode: CurrencyCode.CZK,
              frequency: AmountFrequency.one_time,
            },
            exitFee: data.exitFee ?? 0,
            entryFee: data.entryFee ?? 0,
            managementFee: data.managementFee ?? 0,
            proposedSolutionHorizon: isProposedStepModify ? (data.proposedSolutionHorizon ?? null) : null,
            proposedSolutionEntryFee: isProposedStepModify ? (data.proposedSolutionEntryFee ?? null) : null,
            proposedSolutionManagementFee: isProposedStepModify ? (data.proposedSolutionManagementFee ?? null) : null,
          },
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: getListCurrentContractsQueryKey(analysisId, versionId) });
            queryClient.invalidateQueries({ queryKey: getGetAssetAllocationOverviewQueryKey(analysisId, versionId) });
            onSuccess?.();
          },
          onError: () => {
            toast.error(t(Resources.AnalysisDetail.Toast.ActionError));
          },
        },
      );
    };

    const onEdit = (data: OtherFormType) => {
      if (!currentSolutionId) return;
      const isProposedStepModify = data.proposedStepAction === CurrentContractListItemProposedStepActionType.Modify;
      updateMutation(
        {
          analysisId,
          versionId,
          currentContractGuid: currentSolutionId,
          data: {
            investmentToolType: PatchCurrentContractOtherBodyInvestmentToolType.other,
            solutionHorizonType: horizonType,
            partnerName: institution ?? "",
            proposedStepActionType: data.proposedStepAction as PatchCurrentContractOtherBodyProposedStepActionType,
            oneTimeAmount: {
              value: data.currentValue,
              currencyCode: CurrencyCode.CZK,
              frequency: AmountFrequency.one_time,
            },
            regularAmount: {
              value: data.regularAmount,
              currencyCode: CurrencyCode.CZK,
              frequency: AmountFrequency.monthly,
            },
            oneTimeAmountChange: isProposedStepModify
              ? {
                  value: (data.newDeposit ?? 0) - data.currentValue,
                  currencyCode: CurrencyCode.CZK,
                  frequency: AmountFrequency.one_time,
                }
              : undefined,
            regularAmountChange: isProposedStepModify
              ? {
                  value: (data.newRegularDeposit ?? 0) - data.regularAmount,
                  currencyCode: CurrencyCode.CZK,
                  frequency: AmountFrequency.monthly,
                }
              : undefined,
            assetAllocation: {
              equities: data.equities ?? 0,
              bonds: data.bonds ?? 0,
              commodities: data.commodities ?? 0,
              realEstate: data.realEstate ?? 0,
              cash: data.cash ?? 0,
              others: data.other ?? 0,
            },
            currentSolutionHorizon: data.horizon ?? 0,
            appreciationRate: data.appreciationRate,
            note: data.note ?? "",
            oneTimeFee: {
              value: data.oneTimeFee ?? 0,
              currencyCode: CurrencyCode.CZK,
              frequency: AmountFrequency.one_time,
            },
            exitFee: data.exitFee ?? 0,
            entryFee: data.entryFee ?? 0,
            managementFee: data.managementFee ?? 0,
            proposedSolutionHorizon: isProposedStepModify ? (data.proposedSolutionHorizon ?? null) : null,
            proposedSolutionEntryFee: isProposedStepModify ? (data.proposedSolutionEntryFee ?? null) : null,
            proposedSolutionManagementFee: isProposedStepModify ? (data.proposedSolutionManagementFee ?? null) : null,
          },
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: getListCurrentContractsQueryKey(analysisId, versionId) });
            queryClient.invalidateQueries({ queryKey: getGetAssetAllocationOverviewQueryKey(analysisId, versionId) });
            onSuccess?.();
          },
          onError: () => {
            toast.error(t(Resources.AnalysisDetail.Toast.ActionError));
          },
        },
      );
    };

    const handleProposedStepActionChange = (value: string) => {
      if (value === CurrentContractListItemProposedStepActionType.Modify) {
        const currentForm = getValues();
        const initializationMap: Record<string, any> = {
          newDeposit: currentForm.currentValue,
          newRegularDeposit: currentForm.regularAmount,
          proposedSolutionHorizon: currentForm.horizon,
          proposedSolutionEntryFee: currentForm.entryFee,
          proposedSolutionManagementFee: currentForm.managementFee,
        };

        for (const [field, sourceValue] of Object.entries(initializationMap)) {
          const currentValue = getValues(field as any);
          if (currentValue === undefined || currentValue === null || currentValue === "" || currentValue === 0) {
            setValue(field as any, sourceValue, {
              shouldDirty: true,
            });
          }
        }
      }
    };

    return (
      <FormProvider {...methods}>
        <form>
          <Stack spacing={Spacing.XXL}>
            <Box>
              <Grid container spacing={Spacing.XL}>
                <Grid size={6}>
                  <FormNumberField
                    control={control}
                    name="currentValue"
                    label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.CurrentValue)}
                    required
                    scale={2}
                    thousandsSeparator=" "
                    unit={t(Resources.Common.CZKSuffix)}
                    fullWidth
                  />
                </Grid>
                <Grid size={6}>
                  <FormNumberField
                    control={control}
                    name="regularAmount"
                    label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.RegularAmount)}
                    required
                    scale={2}
                    thousandsSeparator=" "
                    unit={`${t(Resources.Common.CZKSuffix)}/${t(Resources.Common.Month)}`}
                    fullWidth
                  />
                </Grid>

                <Grid size={6}>
                  <FormNumberField
                    control={control}
                    name="horizon"
                    label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.Horizon)}
                    required
                    scale={0}
                    thousandsSeparator=" "
                    unit={t(Resources.Common.Years)}
                    fullWidth
                  />
                </Grid>
                <Grid size={6}>
                  <FormNumberField
                    control={control}
                    name="appreciationRate"
                    label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.AppreciationRate)}
                    required
                    scale={2}
                    thousandsSeparator=" "
                    unit="%"
                    fullWidth
                  />
                </Grid>

                <Stack direction="column">
                  <Box sx={{ width: "100%" }}>
                    <Typography variant="body2" color="secondary" fontWeight={500}>
                      {t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.Fees)}
                    </Typography>
                  </Box>

                  <Grid container spacing={Spacing.XL}>
                    <Grid size={3}>
                      <FormNumberField
                        control={control}
                        name="entryFee"
                        label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.EntryFeeShort)}
                        scale={2}
                        thousandsSeparator=" "
                        unit="%"
                        fullWidth
                      />
                    </Grid>
                    <Grid size={3}>
                      <FormNumberField
                        control={control}
                        name="oneTimeFee"
                        label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.OneTimeFeeShort)}
                        scale={2}
                        thousandsSeparator=" "
                        unit={t(Resources.Common.CZKSuffix)}
                        fullWidth
                      />
                    </Grid>
                    <Grid size={3}>
                      <FormNumberField
                        control={control}
                        name="managementFee"
                        label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.ManagementFeeShort)}
                        scale={2}
                        thousandsSeparator=" "
                        unit="%"
                        fullWidth
                      />
                    </Grid>
                    <Grid size={3}>
                      <FormNumberField
                        control={control}
                        name="exitFee"
                        label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.ExitFeeShort)}
                        scale={2}
                        thousandsSeparator=" "
                        unit="%"
                        fullWidth
                      />
                    </Grid>
                  </Grid>
                </Stack>
              </Grid>
            </Box>

            <Box>
              <CharacteristicsAccordion<OtherFormType>
                key={assetAllocationOverview?.isPortfolioAssetAllocationRequired ? "open" : "closed"}
                defaultExpanded={assetAllocationOverview?.isPortfolioAssetAllocationRequired}
              />
            </Box>

            <Divider />

            <Box>
              <Grid container spacing={Spacing.XL}>
                <Grid size={12}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {t(Resources.AnalysisDetail.GoalsFulfillment.ProposedSolutionSteps)}
                  </Typography>
                </Grid>

                <Grid size={12}>
                  <FormSelect
                    control={control}
                    name="proposedStepAction"
                    label={t(Resources.AnalysisDetail.GoalsFulfillment.ProposedStepActionTypeOptions.Title)}
                    required
                    options={toSelectOptions(proposedStepActionOptions)}
                    placeholder={t(Resources.Common.SelectPlaceholder)}
                    onChange={handleProposedStepActionChange}
                    fullWidth
                  />
                </Grid>

                {proposedStepAction === CurrentContractListItemProposedStepActionType.Modify && (
                  <>
                    <ModifyDepositFields />
                    <Grid size={4}>
                      <FormNumberField
                        control={control}
                        name="proposedSolutionHorizon"
                        label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.ProposedSolutionHorizon)}
                        required
                        scale={0}
                        thousandsSeparator=" "
                        unit={t(Resources.Common.Years)}
                        fullWidth
                      />
                    </Grid>

                    <Grid size={4}>
                      <FormNumberField
                        control={control}
                        name="proposedSolutionEntryFee"
                        label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.ProposedSolutionEntryFee)}
                        scale={2}
                        thousandsSeparator=" "
                        unit="%"
                        fullWidth
                      />
                    </Grid>

                    <Grid size={4}>
                      <FormNumberField
                        control={control}
                        name="proposedSolutionManagementFee"
                        label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.ProposedSolutionManagementFee)}
                        scale={2}
                        thousandsSeparator=" "
                        unit="%"
                        fullWidth
                      />
                    </Grid>
                  </>
                )}
              </Grid>
            </Box>

            <Divider />

            <Box>
              <Grid container spacing={Spacing.XL}>
                <Grid size={12}>
                  <FormTextField
                    control={control}
                    name="note"
                    label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.Note)}
                    maxLength={NOTE_MAX_LENGTH}
                    placeholder={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.NotePlaceholder)}
                    multiline
                    minRows={6}
                    maxRows={8}
                    fullWidth
                  />
                </Grid>
              </Grid>
            </Box>
          </Stack>
        </form>
      </FormProvider>
    );
  },
);
